import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Grid, GridColumn } from "@progress/kendo-react-grid";
import Box from "@mui/material/Box";
import Skeleton from "@mui/material/Skeleton";
import Typography from "@mui/material/Typography";

import { lcvParticipantsConstants } from "../../../../../_redux/_constants/lcvParticipants.constants";
import { lcvParticipantsGetImportHistoryRequest } from "../../../../../_redux/_actions/lcvParticipants.action";

// Previous imports. Paging is a backend parameter, so the response is exactly the page shown.

const INITIAL_PAGE = { skip: 0, take: 10 };

// Mirrors the real row height and the default page size, so the table does not jump when the data
// arrives and the wait does not read as a broken screen.
function LcvImportHistoryGridSkeleton() {
     return (
          <Box>
               {Array.from({ length: INITIAL_PAGE.take }).map((unused, index) => (
                    <Box
                         key={index}
                         sx={{
                              display: "flex",
                              gap: 2,
                              py: 1.5,
                              borderBottom: "1px solid",
                              borderColor: "divider",
                         }}
                    >
                         <Skeleton variant="text" width="38%" />
                         <Skeleton variant="text" width="18%" />
                         <Skeleton variant="text" width="10%" />
                         <Skeleton variant="text" width="10%" />
                         <Skeleton variant="text" width="10%" />
                    </Box>
               ))}
          </Box>
     );
}

export default function LcvImportHistoryGrid() {
     const actionDispatch = useDispatch();

     const history = useSelector((state) => state?.lcvParticipants?.importHistory);

     const isLoading = useSelector((state) =>
          (state?.loading?.pendingRequests || []).includes(
               lcvParticipantsConstants.LCV_PARTICIPANTS_GET_IMPORT_HISTORY_REQUEST,
          ),
     );

     const [page, setPage] = useState(INITIAL_PAGE);

     useEffect(() => {
          actionDispatch(
               lcvParticipantsGetImportHistoryRequest({
                    page: Math.floor(page.skip / page.take) + 1,
                    pageSize: page.take,
               }),
          );
     }, [actionDispatch, page]);

     const rows = history?.items || [];

     return (
          <Box sx={{ mt: 4 }}>
               <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 1 }}>
                    Previous imports
               </Typography>

               {isLoading && rows.length === 0 ? (
                    <LcvImportHistoryGridSkeleton />
               ) : (
                    <Grid
                         data={rows}
                         className="sba-area-data-grid"
                         skip={page.skip}
                         take={page.take}
                         total={history?.totalCount || 0}
                         pageable={{ buttonCount: 4, pageSizes: [10, 25, 50] }}
                         onPageChange={(event) => setPage(event.page)}
                    >
                         <GridColumn field="fileName" title="File" width="380px" />
                         <GridColumn
                              field="uploadedOn"
                              title="Uploaded"
                              width="200px"
                              cells={{
                                   data: (cellProps) => (
                                        <td>
                                             {cellProps.dataItem.uploadedOn
                                                  ? new Date(cellProps.dataItem.uploadedOn).toLocaleString()
                                                  : ""}
                                        </td>
                                   ),
                              }}
                         />
                         <GridColumn field="total" title="Rows" width="100px" />
                         <GridColumn field="succeeded" title="Imported" width="120px" />
                         <GridColumn field="failed" title="Failed" width="110px" />
                         <GridColumn field="skipped" title="Ignored" width="110px" />
                    </Grid>
               )}
          </Box>
     );
}
